import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen, Download, FileText, Plus, Wallet,
  Pencil, Trash2, ArrowRightLeft, LayoutGrid,
} from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import cashbookService from '../../services/CashbookService.js';
import companySettlementService from '../../services/companySettlementService.js';
import CashbookOpeningBalanceModal from './CashbookOpeningBalanceModal.js';
import CashbookEntryModal from './CashbookEntryModal.js';
import CashbookDayBookModal from './CashbookDayBookModal.js';
import CashbookTransferPreviewModal from './CashbookTransferPreviewModal.js';
import { useAuth } from '../../context/AuthContext.js';
import { locationHubService } from '../../services/locationHubService.js';
import { filterShipmentLocations, formatHubBranchLabel, isBranchDestination, isHubDestination } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';
import { formatIndianDate, formatIndianTime, formatTimeAgo, getLocalDateString } from '../../utils/localDate.js';
import SearchableFilterSelect from '../common/SearchableFilterSelect.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';
import { filterDateInputClass } from '../common/uiClasses.js';
import { downloadBlob as saveDownloadedFile } from '../../utils/downloadBlob.js';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const formatCashbookUserOption = (u) => {
  const name = (u?.name || u?.username || u?.user_id || '—').trim();
  const hub = (
    u?.location_hub_label
    || u?.location_hub_name
    || u?.location
    || u?.city
    || '—'
  ).trim();
  const role = (u?.role_name || u?.role || '—').trim();
  return `${name} - ${hub} - ${role}`;
};

const txnStamp = (t) => t?.occurred_at || t?.date || '';

const txnHasClock = (t) => {
  const stamp = String(txnStamp(t)).trim();
  return Boolean(stamp) && !DATE_ONLY.test(stamp);
};

const TxnDateBlock = ({ t, compact = false }) => {
  const stamp = txnStamp(t);
  const dateLabel = formatIndianDate(t?.date || stamp);
  const showClock = txnHasClock(t);
  const timeLabel = showClock ? formatIndianTime(stamp) : '';
  const agoLabel = showClock ? formatTimeAgo(stamp) : '';

  if (compact) {
    return (
      <span className="text-xs text-gray-400">
        {dateLabel}
        {timeLabel ? ` · ${timeLabel}` : ''}
        {agoLabel ? ` · ${agoLabel}` : ''}
      </span>
    );
  }

  return (
    <div className="text-gray-900 leading-5">
      <div className="whitespace-nowrap">{dateLabel}</div>
      {timeLabel ? <div className="whitespace-nowrap">{timeLabel}</div> : null}
      {agoLabel ? (
        <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{agoLabel}</div>
      ) : null}
    </div>
  );
};

const money = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });

const bucketTotal = (b) => {
  if (!b) return 0;
  if (b.total != null && b.total !== '') return Number(b.total) || 0;
  return Number(b.cash || 0) + Number(b.upi ?? b.bank ?? 0);
};

const cashbookModeLabel = (t) => {
  const account = String(t?.account || '').toLowerCase();
  const mode = String(t?.payment_mode || '').toLowerCase();
  if (mode === 'transfer') return 'Transfer';
  if (account === 'cash' || mode === 'cash' || mode === 'petty_cash') return 'Cash';
  return 'UPI';
};

const downloadBlob = (data, filename, type) => saveDownloadedFile(data, filename, type);

const SummaryCard = ({ title, amount, amountColor = 'text-green-600' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
    <p className={`text-xl font-bold ${amountColor}`}>{money(amount)}</p>
  </div>
);

const ModeBadge = ({ t }) => (
  <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] sm:text-xs font-semibold uppercase whitespace-nowrap">
    {cashbookModeLabel(t)}
  </span>
);

const RowMeta = ({ t }) => {
  const recorded = t.recorded_by_name || t.created_by_name;
  const bits = [
    t.agent_name ? `Agent: ${t.agent_name}` : null,
    recorded ? `${t.type === 'received' ? 'Received by' : 'Paid by'}: ${recorded}` : null,
    t.approved_by_name && t.approved_by_name !== recorded
      ? `Approved: ${t.approved_by_name}`
      : null,
    t.location_hub_label,
    t.reason && t.reason !== t.details ? t.reason : null,
  ].filter(Boolean);
  if (!bits.length && t.include_in_totals !== false) return null;
  return (
    <div className="text-[11px] text-gray-400 mt-0.5 space-y-0.5">
      {bits.length > 0 && <div>{bits.join(' · ')}</div>}
      {t.include_in_totals === false && (
        <div className="text-amber-600 font-medium">Audit only (no balance impact)</div>
      )}
    </div>
  );
};

const RowActions = ({ t, onEdit, onDelete }) =>
  t.editable && onEdit && onDelete ? (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={() => onEdit(t)} className="text-gray-400 hover:text-orange-600" title="Edit">
        <Pencil className="w-4 h-4" />
      </button>
      <button type="button" onClick={() => onDelete(t)} className="text-gray-400 hover:text-red-600" title="Delete">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  ) : (
    <span className="text-[10px] text-gray-300">auto</span>
  );

const buildPassbookRows = (opening, txns, showClosing = true) => {
  let balance = bucketTotal(opening);
  const rows = [{
    kind: 'opening',
    particulars: 'Opening Balance',
    balance,
  }];
  (txns || []).forEach((t, i) => {
    const amt = Number(t.amount || 0);
    const isIn = t.type === 'received';
    if (t.include_in_totals !== false) {
      balance = Math.round((balance + (isIn ? amt : -amt)) * 100) / 100;
    }
    const party = t.party_name || '';
    const details = t.details || '';
    let particulars = details || party;
    if (party && details && !details.includes(party)) particulars = `${party} — ${details}`;
    rows.push({
      kind: 'txn',
      t,
      i,
      particulars,
      withdrawal: isIn ? null : amt,
      deposit: isIn ? amt : null,
      balance,
    });
  });
  if (showClosing) {
    rows.push({
      kind: 'closing',
      particulars: 'Closing Balance',
      balance,
    });
  }
  return rows;
};

const PAGE_SIZE = 10;

const PassbookTable = ({ opening, txns, onEdit, onDelete, showReserve = false, showClosing = true }) => {
  const rows = buildPassbookRows(opening, txns, showClosing);

  return (
    <div className="mb-5">
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left sticky top-0 z-10 bg-gray-50">Date</th>
                <th className="px-3 py-2.5 text-left sticky top-0 z-10 bg-gray-50">Particulars</th>
                <th className="px-3 py-2.5 text-left sticky top-0 z-10 bg-gray-50">Type</th>
                {showReserve ? (
                  <th className="px-3 py-2.5 text-right sticky top-0 z-10 bg-gray-50">Reserve</th>
                ) : null}
                <th className="px-3 py-2.5 text-right sticky top-0 z-10 bg-gray-50">Withdrawal</th>
                <th className="px-3 py-2.5 text-right sticky top-0 z-10 bg-gray-50">Deposit</th>
                <th className="px-3 py-2.5 text-right sticky top-0 z-10 bg-gray-50">Balance</th>
                <th className="px-3 py-2.5 text-right w-20 sticky top-0 z-10 bg-gray-50">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, i) => {
                if (row.kind === 'opening' || row.kind === 'closing') {
                  return (
                    <tr key={`${row.kind}-${i}`} className="bg-emerald-50 font-semibold">
                      <td className="px-3 py-2.5">—</td>
                      <td className="px-3 py-2.5">{row.particulars}</td>
                      <td className="px-3 py-2.5">—</td>
                      {showReserve ? <td className="px-3 py-2.5 text-right">—</td> : null}
                      <td className="px-3 py-2.5 text-right">—</td>
                      <td className="px-3 py-2.5 text-right">—</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(row.balance)}</td>
                      <td className="px-3 py-2.5" />
                    </tr>
                  );
                }
                const t = row.t;
                return (
                  <tr key={`${t.source_type}-${t.source_id}-${i}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 align-top"><TxnDateBlock t={t} /></td>
                    <td className="px-3 py-2.5">
                      <div className="text-gray-800">{row.particulars}</div>
                      {t.reference_no ? <div className="text-xs text-gray-500">{t.reference_no}</div> : null}
                      <RowMeta t={t} />
                    </td>
                    <td className="px-3 py-2.5 align-top"><ModeBadge t={t} /></td>
                    {showReserve ? (
                      <td className="px-3 py-2.5 text-right whitespace-nowrap text-amber-700">
                        {Number(t.reserve_amount || 0) !== 0 ? money(t.reserve_amount) : '—'}
                      </td>
                    ) : null}
                    <td className="px-3 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">
                      {row.withdrawal != null ? money(row.withdrawal) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-green-600 whitespace-nowrap">
                      {row.deposit != null ? money(row.deposit) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{money(row.balance)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex justify-end">
                        <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-2">
        {rows.map((row, i) => {
          if (row.kind === 'opening' || row.kind === 'closing') {
            return (
              <div key={`m-${row.kind}-${i}`} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-gray-800">{row.particulars}</p>
                <p className="text-sm mt-1 font-semibold">{money(row.balance)}</p>
              </div>
            );
          }
          const t = row.t;
          return (
            <div
              key={`m-${t.source_type}-${t.source_id}-${i}`}
              className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
            >
              <p className="text-sm font-medium text-gray-800 break-words">{row.particulars}</p>
              <RowMeta t={t} />
              <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                <span className="text-red-600">Wdl {row.withdrawal != null ? money(row.withdrawal) : '—'}</span>
                <span className="text-green-600 text-right">Dep {row.deposit != null ? money(row.deposit) : '—'}</span>
                {showReserve ? (
                  <span className="text-amber-700 col-span-2">
                    Reserve {Number(t.reserve_amount || 0) !== 0 ? money(t.reserve_amount) : '—'}
                  </span>
                ) : null}
                <span className="col-span-2 text-right font-semibold">Bal {money(row.balance)}</span>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <TxnDateBlock t={t} compact />
                  <ModeBadge t={t} />
                </div>
                <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const signedInRole = (user) => {
  if (user?.role) return user.role;
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null')?.role || null;
  } catch {
    return null;
  }
};

const OWN_BRANCH_DEFAULT_ROLES = ['Admin', 'Accountant'];
const HUB_PICKER_ROLES = ['System Admin', 'Admin', 'Accountant'];

const defaultBranchId = (user) => {
  if (OWN_BRANCH_DEFAULT_ROLES.includes(signedInRole(user)) && user?.location_hub_id) {
    return String(user.location_hub_id);
  }
  return '';
};

const CashbookReport = () => {
  const { user } = useAuth();
  const isHubAdmin = user?.role === 'Hub Admin';
  const isAgent = user?.role === 'Agent';
  const canPickHub = HUB_PICKER_ROLES.includes(user?.role);
  const canFilterUser = canPickHub || isHubAdmin;
  const canEdit = !isAgent;
  const todayDefault = getLocalDateString();
  const [fromDate, setFromDate] = useState(todayDefault);
  const [toDate, setToDate] = useState(todayDefault);
  const [locationHubId, setLocationHubId] = useState(() => defaultBranchId(user));
  const [filtersReady, setFiltersReady] = useState(false);
  const appliedUserDefaults = useRef(false);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [locationHubs, setLocationHubs] = useState([]);
  const [hubUsers, setHubUsers] = useState([]);
  const [branchAgents, setBranchAgents] = useState([]);
  const [agentOwnerIds, setAgentOwnerIds] = useState(() => (
    signedInRole(user) === 'Agent' && user?.user_id ? [String(user.user_id)] : []
  ));
  const [report, setReport] = useState(null);
  const [txns, setTxns] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const txnsRef = useRef([]);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const requestSeq = useRef(0);
  const sentinelRef = useRef(null);
  const [exporting, setExporting] = useState('');
  const [transferPair, setTransferPair] = useState(null);
  const [transferToId, setTransferToId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferPreviewOpen, setTransferPreviewOpen] = useState(false);
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferPreviewLoading, setTransferPreviewLoading] = useState(false);
  const [transferOpenDuesLoading, setTransferOpenDuesLoading] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [dayBookOpen, setDayBookOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    if (!user?.user_id) {
      setFiltersReady(false);
      appliedUserDefaults.current = false;
      return;
    }
    if (!appliedUserDefaults.current && OWN_BRANCH_DEFAULT_ROLES.includes(user.role)) {
      appliedUserDefaults.current = true;
      const today = getLocalDateString();
      setFromDate(today);
      setToDate(today);
      if (user.location_hub_id) {
        setLocationHubId(String(user.location_hub_id));
      }
    }
    if (!appliedUserDefaults.current) {
      appliedUserDefaults.current = true;
    }
    setFiltersReady(true);
  }, [user?.user_id, user?.role, user?.location_hub_id]);

  const scopedHubId = useMemo(() => {
    if (isAgent || isHubAdmin) {
      return user?.location_hub_id ? String(user.location_hub_id) : '';
    }
    if (canPickHub) {
      return locationHubId ? String(locationHubId) : '';
    }
    return '';
  }, [isAgent, isHubAdmin, canPickHub, user?.location_hub_id, locationHubId]);

  useEffect(() => {
    if (isAgent) return;
    import('../../services/companySettingsService.js')
      .then((mod) => mod.default.getSettings())
      .then((res) => {
        const data = res?.data || {};
        const fromIds = Array.isArray(data.settlement_collect_hub_ids) && data.settlement_collect_hub_ids.length
          ? data.settlement_collect_hub_ids.map(String)
          : (data.settlement_collect_hub_id ? [String(data.settlement_collect_hub_id)] : []);
        const toIds = Array.isArray(data.settlement_admin_transfer_hub_ids) && data.settlement_admin_transfer_hub_ids.length
          ? data.settlement_admin_transfer_hub_ids.map(String)
          : (data.settlement_admin_transfer_hub_id ? [String(data.settlement_admin_transfer_hub_id)] : []);
        if (!fromIds.length || !toIds.length) {
          setTransferPair(null);
          return;
        }
        setTransferPair({
          fromIds,
          toIds,
        });
      })
      .catch(() => setTransferPair(null));
  }, [isAgent]);

  useEffect(() => {
    if (!canPickHub && !isHubAdmin) return;
    locationHubService.getHubsForSelect({ brief: 1 })
      .then((res) => {
        const hubs = res.data?.hubs || [];
        const usable = hubs.filter((hub) => (
          !hub?.destination_type
          || isBranchDestination(hub.destination_type)
          || isHubDestination(hub.destination_type)
        ));
        setLocationHubs(usable.length ? usable : filterShipmentLocations(hubs));
      })
      .catch(() => setLocationHubs([]));
  }, [canPickHub, isHubAdmin]);

  useEffect(() => {
    if (!canFilterUser) return undefined;
    let active = true;
    setHubUsers([]);
    const hubId = scopedHubId || (locationHubId ? String(locationHubId) : '');
    import('../../services/UserService.js')
      .then(({ default: userService }) =>
        userService.getAll(1, 200, {
          ...(hubId ? { location_hub_id: hubId, all: 'true' } : {}),
          role: 'Agent',
        }),
      )
      .then((res) => {
        if (!active) return;
        const items = res?.items || res?.data?.items || res?.data || [];
        const rows = Array.isArray(items) ? items : [];
        setHubUsers(hubId
          ? rows.filter((row) => String(row?.location_hub_id || '') === hubId)
          : rows);
      })
      .catch(() => active && setHubUsers([]));
    return () => { active = false; };
  }, [canFilterUser, locationHubId, scopedHubId]);

  useEffect(() => {
    if (!isAgent) return undefined;
    let active = true;
    cashbookService.getBranchAgents()
      .then((res) => {
        if (!active) return;
        const rows = res?.data?.data || [];
        setBranchAgents(Array.isArray(rows) ? rows : []);
      })
      .catch(() => active && setBranchAgents([]));
    return () => { active = false; };
  }, [isAgent]);

  const showAgentUserFilter = isAgent && branchAgents.length > 1;
  const agentOwnerParam = useMemo(() => {
    if (!showAgentUserFilter) return undefined;
    const allowed = new Set(branchAgents.map((row) => String(row.user_id)));
    const picked = agentOwnerIds.map(String).filter((id) => allowed.has(id));
    if (!picked.length) return undefined;
    if (picked.length === 1 && picked[0] === String(user?.user_id || '')) return undefined;
    return picked;
  }, [showAgentUserFilter, branchAgents, agentOwnerIds, user?.user_id]);

  const fetchReport = useCallback(async ({ append = false } = {}) => {
    if (!user?.user_id || !filtersReady) return;
    const seq = append ? requestSeq.current : requestSeq.current + 1;
    if (!append) requestSeq.current = seq;
    const offset = append ? txnsRef.current.length : 0;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const hubParam = scopedHubId || undefined;
      const ownerParam = canFilterUser && ownerUserId ? ownerUserId : undefined;
      const accountParam = accountFilter || undefined;
      const res = await cashbookService.getRange(
        fromDate, toDate, hubParam, ownerParam, accountParam, agentOwnerParam,
        { limit: PAGE_SIZE, offset },
      );
      if (seq !== requestSeq.current) return;
      const data = res.data?.data || null;
      const pageTxns = data?.transactions || [];
      const nextTxns = append ? [...txnsRef.current, ...pageTxns] : pageTxns;
      txnsRef.current = nextTxns;
      hasMoreRef.current = Boolean(data?.has_more);
      setTxns(nextTxns);
      setHasMore(Boolean(data?.has_more));
      setReport((prev) => (append && prev ? { ...prev, ...data, transactions: nextTxns } : data));
    } catch (err) {
      if (seq !== requestSeq.current) return;
      Alert.error(err.response?.data?.error || 'Failed to load cashbook');
      if (!append) {
        txnsRef.current = [];
        hasMoreRef.current = false;
        setTxns([]);
        setHasMore(false);
        setReport(null);
      }
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    }
  }, [
    user?.user_id, fromDate, toDate, ownerUserId, accountFilter,
    filtersReady, scopedHubId, canFilterUser, agentOwnerParam,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    fetchReport({ append: true });
  }, [fetchReport]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { root: null, rootMargin: '240px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, loading, loadingMore, hasMore, txns.length]);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const hubParam = scopedHubId || undefined;
      const ownerParam = canFilterUser && ownerUserId ? ownerUserId : undefined;
      const accountParam = accountFilter || undefined;
      let saved;
      if (kind === 'pdf') {
        const res = await cashbookService.exportPdf(
          fromDate, toDate, hubParam, ownerParam, accountParam, agentOwnerParam,
        );
        saved = await downloadBlob(res.data, `cashbook_${fromDate}_${toDate}.pdf`, 'application/pdf');
      } else {
        const res = await cashbookService.exportExcel(
          fromDate, toDate, hubParam, ownerParam, accountParam, agentOwnerParam,
        );
        saved = await downloadBlob(
          res.data,
          `cashbook_${fromDate}_${toDate}.xlsx`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
      }
      if (saved?.method === 'cancelled') return;
      Alert.success(`Cashbook ${kind.toUpperCase()} exported`);
    } catch (err) {
      Alert.error(`Failed to export ${kind.toUpperCase()}`);
    } finally {
      setExporting('');
    }
  };

  const handleDeleteEntry = async (t) => {
    const ok = await Alert.confirm('Delete this cashbook entry?', 'Delete Entry');
    if (!ok) return;
    try {
      await cashbookService.deleteEntry(t.id);
      Alert.success('Entry deleted');
      fetchReport();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  useEffect(() => {
    const ids = transferPair?.toIds || [];
    setTransferToId((prev) => (ids.includes(prev) ? prev : (ids[0] || '')));
  }, [transferPair]);

  const viewingCollectHub = Boolean(
    transferPair
    && !ownerUserId
    && (
      (canPickHub && transferPair.fromIds.includes(String(scopedHubId || locationHubId)))
      || (!canPickHub && isHubAdmin && transferPair.fromIds.includes(String(user?.location_hub_id || '')))
    ),
  );
  const hubLabel = (hubId) => {
    const hub = locationHubs.find((row) => String(row.id) === String(hubId));
    return hub ? (formatHubBranchLabel(hub) || hub.location || 'Admin branch') : 'the admin branch';
  };
  const transferToName = hubLabel(transferToId);

  const handleTransfer = async () => {
    if (!transferToId) {
      Alert.error('Choose the admin branch to transfer to');
      return;
    }
    const hubId = scopedHubId || user?.location_hub_id;
    setTransferPreviewOpen(true);
    setTransferPreview(null);
    setTransferPreviewLoading(true);
    setTransferOpenDuesLoading(true);
    let pendingOpenAgents = null;
    const mergeOpenDues = (agents) => {
      if (!agents) return;
      pendingOpenAgents = agents;
      const open = agents
        .filter((row) => Number(row?.amount_due || 0) > 0.009)
        .map((row) => ({
          user_id: row.user_id,
          name: row.name,
          amount_due: row.amount_due,
        }));
      setTransferPreview((prev) => (prev ? {
        ...prev,
        open_agent_dues: open,
        open_agent_dues_total: open.reduce((sum, row) => sum + Number(row.amount_due || 0), 0),
      } : prev));
    };
    try {
      const previewPromise = cashbookService.previewTransferToAdmin(hubId, transferToId);
      companySettlementService.listBranchAgents(hubId, { transferPreview: true })
        .then((body) => mergeOpenDues(body?.data?.agents))
        .catch(() => {})
        .finally(() => setTransferOpenDuesLoading(false));
      const res = await previewPromise;
      setTransferPreview(res?.data?.data || res?.data || null);
      mergeOpenDues(pendingOpenAgents);
    } catch (err) {
      setTransferPreviewOpen(false);
      Alert.error(err.response?.data?.error || 'Could not load transfer preview');
    } finally {
      setTransferPreviewLoading(false);
    }
  };

  const confirmTransfer = async () => {
    if (!transferToId || !transferPreview?.can_transfer) return;
    try {
      setTransferring(true);
      const hubId = scopedHubId || user?.location_hub_id;
      const res = await cashbookService.transferToAdmin(hubId, transferToId);
      Alert.success(res?.data?.message || 'Balance transferred');
      setTransferPreviewOpen(false);
      fetchReport();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const openAddEntry = () => { setEditingEntry(null); setEntryOpen(true); };
  const openEditEntry = (t) => { setEditingEntry(t); setEntryOpen(true); };

  const opening = report?.opening || { cash: 0, bank: 0 };
  const received = report?.received || { cash: 0, bank: 0 };
  const paid = report?.paid || { cash: 0, bank: 0 };
  const current = report?.current || report?.closing || { cash: 0, bank: 0 };
  const closing = report?.closing || current;
  const todayTotal = Math.round((bucketTotal(opening) - bucketTotal(current)) * 100) / 100;
  const shownCount = txns.length;
  const totalCount = Number(report?.total_count ?? shownCount) || 0;

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-buttonhoverTxt" />
              Cashbook
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {report?.book_label
                ? `Book: ${report.book_label}`
                : 'Passbook with a running balance'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
          {canEdit && viewingCollectHub && (
            <>
                  {(transferPair?.toIds || []).length > 1 && (
                    <select
                      value={transferToId}
                      onChange={(e) => setTransferToId(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      aria-label="Admin branch"
                    >
                      {(transferPair.toIds || []).map((id) => (
                        <option key={id} value={id}>{hubLabel(id)}</option>
                      ))}
                    </select>
                  )}
                  <Button
                    variant="secondary"
                    icon={ArrowRightLeft}
                    onClick={handleTransfer}
                    loading={transferring}
                    disabled={transferring}
                  >
                    Transfer
                  </Button>
                </>
          )}
            <Button variant="secondary" icon={LayoutGrid} onClick={() => setDayBookOpen(true)}>
              Day Book
            </Button>
          {canEdit && (
            <>
              <Button variant="secondary" icon={Wallet} onClick={() => setOpeningOpen(true)}>Opening Balance</Button>
              <Button variant="primary" icon={Plus} onClick={openAddEntry}>Add Entry</Button>
            </>
          )}
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm mb-4">
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 [&>*]:min-w-0">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={filterDateInputClass}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className={filterDateInputClass}
              />
            </div>
            {canPickHub && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hub / Branch</label>
                <SearchableFilterSelect
                  value={locationHubId}
                  onChange={(e) => {
                    setLocationHubId(e.target.value);
                    setOwnerUserId('');
                  }}
                  placeholder={OWN_BRANCH_DEFAULT_ROLES.includes(user?.role) ? 'My branch' : 'All hubs'}
                  options={[
                    { value: '', label: 'All hubs' },
                    ...sortLocationsByNumber(locationHubs).map((hub) => ({
                      value: hub.id,
                      label: formatLocationDropdownLabel(hub, true),
                    })),
                  ]}
                />
              </div>
            )}
            {canFilterUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User book</label>
                <SearchableFilterSelect
                  value={ownerUserId}
                  onChange={(e) => setOwnerUserId(e.target.value)}
                  placeholder="All users"
                  options={[
                    { value: '', label: 'All users' },
                    ...hubUsers.map((u) => ({
                      value: u.user_id || u.id,
                      label: formatCashbookUserOption(u),
                    })),
                  ]}
                />
              </div>
            )}
            {isHubAdmin && !canPickHub && (
              <div className="col-span-2 lg:col-span-1 flex items-end">
                <p className="text-xs text-gray-500 pb-2">Showing your hub/branch cashbook</p>
              </div>
            )}
            {showAgentUserFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <SearchableFilterSelect
                  multiple
                  value={agentOwnerIds}
                  onChange={(e) => {
                    const next = Array.isArray(e.target.value) ? e.target.value.map(String) : [];
                    if (!next.length) {
                      setAgentOwnerIds(branchAgents.map((row) => String(row.user_id)));
                      return;
                    }
                    setAgentOwnerIds(next);
                  }}
                  placeholder="My cashbook"
                  emptyLabel="All branch agents"
                  options={branchAgents.map((row) => ({
                    value: String(row.user_id),
                    label: formatCashbookUserOption(row),
                  }))}
                />
              </div>
            )}
            {isAgent && !showAgentUserFilter && (
              <div className="col-span-2 lg:col-span-1 flex items-end">
                <p className="text-xs text-gray-500 pb-2">Your personal cashbook</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cash / UPI</label>
              <SearchableFilterSelect
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                placeholder="All modes"
                options={[
                  { value: '', label: 'All modes' },
                  { value: 'cash', label: 'Cash' },
                  { value: 'upi', label: 'UPI' },
                ]}
              />
            </div>
            <div className="flex items-end">
              <Button variant="secondary" icon={FileText} fullWidth
                onClick={() => handleExport('pdf')} loading={exporting === 'pdf'} disabled={!!exporting}>
                PDF
              </Button>
            </div>
            <div className="flex items-end">
              <Button variant="secondary" icon={Download} fullWidth
                onClick={() => handleExport('excel')} loading={exporting === 'excel'} disabled={!!exporting}>
                Excel
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader fullScreen={false} message="Loading cashbook..." />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
              <SummaryCard title="Opening Balance" amount={bucketTotal(opening)} />
              <SummaryCard title="Total Received" amount={bucketTotal(received)} />
              <SummaryCard title="Total Paid" amount={bucketTotal(paid)} amountColor="text-red-600" />
              <SummaryCard title="Current Balance" amount={bucketTotal(closing)} />
              <SummaryCard
                title="Today's Total"
                amount={todayTotal}
                amountColor={todayTotal < 0 ? 'text-red-600' : 'text-green-600'}
              />
            </div>
            <div className="sticky top-0 z-30 bg-gray-50 pt-1 pb-2 -mx-1 px-1">
              <h3 className="text-base font-bold text-gray-700 mb-2">
                Passbook
                {totalCount > 0 && (
                  <span className="ml-2 text-sm font-medium text-gray-500">
                    {shownCount} of {totalCount}
                  </span>
                )}
              </h3>
            </div>
            <PassbookTable
              opening={opening}
              txns={txns}
              onEdit={canEdit ? openEditEntry : undefined}
              onDelete={canEdit ? handleDeleteEntry : undefined}
              showReserve={!!report?.labour_reserve_enabled}
              showClosing={!hasMore}
            />
            {(loadingMore || hasMore) && (
              <div ref={sentinelRef} className="py-3 text-center text-sm text-gray-500">
                {loadingMore ? 'Loading more entries...' : 'Scroll for more'}
              </div>
            )}
          </>
        )}
      </div>

      <CashbookDayBookModal
        open={dayBookOpen}
        onClose={() => setDayBookOpen(false)}
        fromDate={fromDate}
        toDate={toDate}
        locationHubId={scopedHubId || undefined}
        ownerUserId={ownerUserId || undefined}
        ownerUserIds={agentOwnerParam}
        branchLabel={report?.book_label || hubLabel(scopedHubId || locationHubId)}
      />

      <CashbookTransferPreviewModal
        open={transferPreviewOpen}
        preview={transferPreview}
        loading={transferPreviewLoading}
        openDuesLoading={transferOpenDuesLoading}
        onClose={() => setTransferPreviewOpen(false)}
        onConfirm={confirmTransfer}
        confirming={transferring}
      />

      {canEdit && (
        <>
          <CashbookOpeningBalanceModal
            open={openingOpen}
            onClose={() => setOpeningOpen(false)}
            onSaved={fetchReport}
            locationHubId={scopedHubId || undefined}
            ownerUserId={ownerUserId || undefined}
          />
          <CashbookEntryModal
            open={entryOpen}
            entry={editingEntry}
            onClose={() => { setEntryOpen(false); setEditingEntry(null); }}
            onSaved={fetchReport}
            locationHubs={locationHubs}
            defaultLocationHubId={scopedHubId || ''}
            hubUsers={hubUsers}
            defaultOwnerUserId={ownerUserId || ''}
            requireHub
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default CashbookReport;
